/**
 * SKOP Graduation Timer
 *
 * Predicts when a token will graduate from bonding curve to DEX
 * based on current progress and recent buy rate.
 */

// Bonding curve constants (pump.fun)
const GRADUATION_THRESHOLD = 85_000_000; // 85M tokens sold = graduation

export interface GraduationPrediction {
  // Current state
  currentProgress: number; // 0-100%
  tokensSold: number;
  tokensRemaining: number;

  // Prediction
  estimatedTimeMs: number | null; // null if insufficient data
  estimatedTimeFormatted: string;
  confidence: 'high' | 'medium' | 'low' | 'unknown';

  // Rate analysis
  buyRatePerHour: number; // tokens per hour
  buyRatePerMinute: number;
  recentBuys: number; // buys in last hour

  // Status
  isGraduated: boolean;
  graduatedAt?: number;
}

export interface RecentTrade {
  timestamp: number;
  type: 'buy' | 'sell';
  tokenAmount: number;
  solAmount: number;
}

/**
 * Calculate graduation prediction based on bonding curve state and recent trades
 */
export function predictGraduation(
  bondingCurveProgress: number, // 0-100
  recentTrades: RecentTrade[],
  isGraduated: boolean = false
): GraduationPrediction {
  // If already graduated, return completed state
  if (isGraduated || bondingCurveProgress >= 100) {
    return {
      currentProgress: 100,
      tokensSold: GRADUATION_THRESHOLD,
      tokensRemaining: 0,
      estimatedTimeMs: 0,
      estimatedTimeFormatted: 'Graduated!',
      confidence: 'high',
      buyRatePerHour: 0,
      buyRatePerMinute: 0,
      recentBuys: 0,
      isGraduated: true,
    };
  }

  // Calculate current state
  const tokensSold = (bondingCurveProgress / 100) * GRADUATION_THRESHOLD;
  const tokensRemaining = GRADUATION_THRESHOLD - tokensSold;
  const currentProgress = bondingCurveProgress;

  // Filter to buys only in the last hour
  const oneHourAgo = Date.now() - 60 * 60 * 1000;
  const recentBuys = recentTrades.filter(
    (t) => t.type === 'buy' && t.timestamp > oneHourAgo
  );

  // Calculate buy rate
  let buyRatePerHour = 0;
  let buyRatePerMinute = 0;
  let confidence: GraduationPrediction['confidence'] = 'unknown';

  if (recentBuys.length >= 2) {
    // Calculate tokens bought in the time window
    const totalTokensBought = recentBuys.reduce((sum, t) => sum + t.tokenAmount, 0);
    const timeSpanMs = Date.now() - Math.min(...recentBuys.map((t) => t.timestamp));
    const timeSpanHours = timeSpanMs / (60 * 60 * 1000);

    if (timeSpanHours > 0) {
      buyRatePerHour = totalTokensBought / timeSpanHours;
      buyRatePerMinute = buyRatePerHour / 60;

      // Determine confidence based on sample size and time span
      if (recentBuys.length >= 10 && timeSpanHours >= 0.5) {
        confidence = 'high';
      } else if (recentBuys.length >= 5 && timeSpanHours >= 0.25) {
        confidence = 'medium';
      } else {
        confidence = 'low';
      }
    }
  }

  // Calculate estimated time to graduation
  let estimatedTimeMs: number | null = null;
  let estimatedTimeFormatted = 'Calculating...';

  if (buyRatePerHour > 0) {
    const hoursRemaining = tokensRemaining / buyRatePerHour;
    estimatedTimeMs = hoursRemaining * 60 * 60 * 1000;
    estimatedTimeFormatted = formatTimeRemaining(estimatedTimeMs);
  } else if (recentBuys.length === 0) {
    estimatedTimeFormatted = 'No recent activity';
  } else {
    estimatedTimeFormatted = 'Insufficient data';
  }

  return {
    currentProgress,
    tokensSold,
    tokensRemaining,
    estimatedTimeMs,
    estimatedTimeFormatted,
    confidence,
    buyRatePerHour,
    buyRatePerMinute,
    recentBuys: recentBuys.length,
    isGraduated: false,
  };
}

/**
 * Format milliseconds into human-readable time
 */
function formatTimeRemaining(ms: number): string {
  if (ms <= 0) return 'Any moment now!';

  const minutes = Math.floor(ms / (60 * 1000));
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 7) {
    return `${days}+ days`;
  } else if (days > 0) {
    const remainingHours = hours % 24;
    return `~${days}d ${remainingHours}h`;
  } else if (hours > 0) {
    const remainingMinutes = minutes % 60;
    return `~${hours}h ${remainingMinutes}m`;
  } else if (minutes > 0) {
    return `~${minutes}m`;
  } else {
    return '< 1 minute';
  }
}

/**
 * Get confidence color for UI
 */
export function getConfidenceColor(confidence: GraduationPrediction['confidence']): string {
  switch (confidence) {
    case 'high':
      return 'var(--success)';
    case 'medium':
      return 'var(--warning)';
    case 'low':
      return 'var(--error)';
    default:
      return 'var(--text-tertiary)';
  }
}

/**
 * Get confidence label for UI
 */
export function getConfidenceLabel(confidence: GraduationPrediction['confidence']): string {
  switch (confidence) {
    case 'high':
      return 'High confidence';
    case 'medium':
      return 'Medium confidence';
    case 'low':
      return 'Low confidence';
    default:
      return 'Calculating...';
  }
}

/**
 * Generate mock recent trades for demonstration
 */
export function generateMockRecentTrades(count: number = 20): RecentTrade[] {
  const trades: RecentTrade[] = [];
  const now = Date.now();

  for (let i = 0; i < count; i++) {
    const isBuy = Math.random() > 0.3; // 70% buys
    const solAmount = 0.1 + Math.random() * 2;

    trades.push({
      timestamp: now - (i * (1 + Math.random() * 5) * 60 * 1000), // 1-6 minutes apart
      type: isBuy ? 'buy' : 'sell',
      tokenAmount: solAmount * (500000 + Math.random() * 2000000),
      solAmount,
    });
  }

  return trades.sort((a, b) => b.timestamp - a.timestamp);
}
